import { useState, useMemo, useEffect } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { useVideo } from "../../../hooks/useVideo";
import ConfirmationModal from "../../confirmation/ConfirmationModal";
import { generatePageNumbers } from "../../../util/generatePageNumber";
import LoadingSpinner from "../../loader/Loader";
import {
  useDeleteFaqMutation,
  useGetAllFaqsQuery,
} from "../../../features/api/faqApiSlice";

const AdminFaqList = () => {
  const navigate = useNavigate();
  const { formatDate } = useVideo();
  const [faqToDelete, setFaqToDelete] = useState(null);
  const [deleteFaq] = useDeleteFaqMutation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // ✅ Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8; // categories per page

  const {
    data: faqs = [], // ✅ safe default so maps never blow up
    isFetching, // use this for spinner
    isError,
    isLoading,
    refetch,
  } = useGetAllFaqsQuery(undefined, {
    refetchOnMountOrArgChange: "always", // ✅ refetch every time page mounts
    refetchOnFocus: true, // refresh when tab regains focus
    refetchOnReconnect: true, // refresh when network comes back
  });

  useEffect(() => {
    if (!faqs?.length && !isFetching && !isError) {
      refetch(); // one more nudge in case the first request was aborted in dev
    }
  }, [faqs?.length, isFetching, isError, refetch]);

  const [videoFilter, setVideoFilter] = useState("");

  const totalPages = Math.ceil(faqs.length / pageSize);

  const currentFaqs = useMemo(() => {
    const startIdx = (currentPage - 1) * pageSize;
    return faqs.slice(startIdx, startIdx + pageSize);
  }, [faqs, currentPage, pageSize]);

  const pageNumbers = useMemo(
    () => generatePageNumbers(currentPage, totalPages),
    [currentPage, totalPages]
  );

  const handlePageClick = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteClick = (faqId) => {
    setFaqToDelete(faqId);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!faqToDelete) return;
    try {
      const response = await deleteFaq({ faqId: faqToDelete }).unwrap();
      toast.success(response?.message || "Faq deleted successfully!");
      setFaqToDelete(null);
      setIsModalOpen(false);
    } catch (err) {
      console.error("Failed to delete faq:", err);
      toast.error(err?.data?.message || "Failed to delete faq.");
    }
  };

  const handleCancelDelete = () => {
    setFaqToDelete(null);
    setIsModalOpen(false);
  };

  if (isLoading) return <LoadingSpinner />;

  if (!faqs || faqs?.length === 0) {
    return (
      <div className="no-content">
        <div>
          <p>No faqs found</p>
        </div>
      </div>
    );
  } else if (isError)
    return (
      <div className="no-content">
        <div>
          <p>Error loading videos</p>
        </div>
      </div>
    );

  return (
    <div className="transactions-container">
      <div className="dashboard-top-section">
        <div className="dash-top-flex">
          <h1 className="dashboard-title">Total Faqs {faqs?.length}</h1>
          <button onClick={() => navigate("/admin/faqs/addfaq")}>
            Add Faq
          </button>
        </div>
      </div>
      <div className="filters">
        <input
          type="text"
          placeholder="Filter by first or last name"
          value={videoFilter}
          onChange={(e) => setVideoFilter(e.target.value)}
        />
      </div>

      <table className="transactions-table">
        <thead>
          <tr>
            <th>No.</th>
            <th>Question</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentFaqs?.map((tx, index) => (
            <tr key={tx?._id}>
              <td>{index + 1}</td>
              <td>{tx?.question}</td>
              <td>{formatDate(tx?.createdAt)}</td>
              <td className="actions-container">
                <button
                  className="refund-btn"
                  onClick={() => handleDeleteClick(tx?.id || tx?._id)}
                >
                  Delete
                </button>
                <Link
                  className="ed-button"
                  aria-label={`edit ${tx?.videoTitle}`}
                  to={`/admin/faqs/editfaq/${tx?._id}`}
                >
                  Edit
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
        <ConfirmationModal
          isOpen={isModalOpen}
          message="Are you sure you want to delete this faq? This action cannot be undone."
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      </table>
      {/* ✅ Pagination */}
      {totalPages > 1 && (
        <div className="pagination responsive-pagination">
          <button
            onClick={() => handlePageClick(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1}
          >
            {"<"}
          </button>
          {pageNumbers.map((page, idx) =>
            page === "..." ? (
              <span className="ellipsis" key={idx}>
                ...
              </span>
            ) : (
              <button
                key={idx}
                className={currentPage === page ? "active" : ""}
                onClick={() => handlePageClick(page)}
              >
                {page}
              </button>
            )
          )}
          <button
            onClick={() =>
              handlePageClick(Math.min(currentPage + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          >
            {">"}
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminFaqList;
